"use client";

/**
 * SimpleDisplay - Default fallback display for unmapped tools
 *
 * Shows tool input and output in a JSON viewer format.
 * Used when no specific display component exists for a tool.
 */

import { useState } from "react";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import { InfoOutlined } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

export function SimpleDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);

  const { status, isLoading, isError, error, output } = toolState;
  const { toolName, input, title } = toolCall;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={title || ""}
        toolName={toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<InfoOutlined style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded}>
        {/* Loading state */}
        {isLoading && (
          <div style={{ marginBottom: spacing[4] }}>
            <ToolProgress variant="dots" label="Processing..." />
          </div>
        )}

        {/* Error state */}
        {isError && error && <ToolError error={error} />}

        {/* Input section */}
        {input !== undefined && (
          <div style={{ marginBottom: spacing[4] }}>
            <span
              style={{
                display: "block",
                marginBottom: spacing[2],
                ...textStyles.label,
                fontWeight: typography.fontWeight.medium,
                color: colors.text.muted,
              }}
            >
              Parameters
            </span>
            <pre
              style={{
                padding: spacing[3],
                borderRadius: radius.sm,
                backgroundColor: primitives.neutral[100],
                fontFamily: typography.fontFamily.mono,
                fontSize: `${typography.scale.xs[0]}px`,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 200,
                margin: 0,
              }}
            >
              {JSON.stringify(input, null, 2)}
            </pre>
          </div>
        )}

        {/* Output section */}
        {output !== null && output !== undefined && (
          <div>
            <span
              style={{
                display: "block",
                marginBottom: spacing[2],
                ...textStyles.label,
                fontWeight: typography.fontWeight.medium,
                color: colors.text.muted,
              }}
            >
              Result
            </span>
            <pre
              style={{
                padding: spacing[3],
                borderRadius: radius.sm,
                backgroundColor: primitives.neutral[100],
                fontFamily: typography.fontFamily.mono,
                fontSize: `${typography.scale.xs[0]}px`,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 400,
                margin: 0,
              }}
            >
              {typeof output === "string"
                ? output
                : JSON.stringify(output, null, 2)}
            </pre>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && output === null && input === undefined && (
          <ToolEmptyState icon={<InfoOutlined />} message="No data available" />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default SimpleDisplay;
